import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  TextField,
  Link,
  InputAdornment,
  IconButton,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  Paper,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  AppBar,
  Badge,
  Breadcrumbs,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Menu,
  Toolbar,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@/store/hooks";

interface BreadcrumbsComponentProps {
  overrideDeviceLabel?: string;
}

const BreadcrumbsComponent: React.FC<BreadcrumbsComponentProps> = ({ overrideDeviceLabel }) => {
  const { isAuthenticated } = useAuth0();
  let location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [deviceDetails, setDeviceDetails] = useState<{ [key: string]: string }>(
    {}
  );
  // Get current device info from Redux store
  const deviceInfo = useAppSelector((state) => state.device);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const pathnames = location.pathname.split("/").filter((x) => x);

  // Security: Whitelist of allowed route segments to prevent open redirect vulnerability
  const allowedRoutes = [
    'devices',
    'dashboard',
    'reports',
    'settings',
    'my-tasks',
    'walkdowns',
    'valve-condition-monitoring',
    'site-documentation',
    'turnarounds',
    'search-results'
  ];

  // Security: Validate and sanitize navigation path
  const safeNavigate = (path: string) => {
    // Extract path segments
    const segments = path.split('/').filter(x => x);
    
    // Check if the first segment is in the allowed routes
    if (segments.length === 0) {
      navigate('/devices'); // Default safe route
      return;
    }

    const firstSegment = segments[0].toLowerCase();
    
    // If first segment is not allowed, redirect to safe default
    if (!allowedRoutes.includes(firstSegment)) {
      console.warn(`Attempted navigation to unauthorized route: ${path}`);
      navigate('/devices');
      return;
    }

    // Additional validation: ensure path doesn't contain suspicious patterns
    const suspiciousPatterns = [
      /^https?:\/\//i,  // External URLs
      /^\/\//,           // Protocol-relative URLs
      /javascript:/i,    // JavaScript protocol
      /data:/i,          // Data protocol
      /@/,               // Email-like patterns
    ];

    if (suspiciousPatterns.some(pattern => pattern.test(path))) {
      console.warn(`Blocked suspicious navigation pattern: ${path}`);
      navigate('/devices');
      return;
    }

    // Safe to navigate - use relative path
    navigate(path);
  };

  // Get current turnaround detail from Redux store
  const currentTurnaroundId =
    pathnames[0] === "turnarounds" && pathnames.length >= 2
      ? pathnames[1]
      : null;
  const turnAroundDetail = useAppSelector((state) =>
    currentTurnaroundId
      ? state.turnaroundDetail.details[currentTurnaroundId]?.data
      : null
  );

  console.log("Bread: deviceid");

  // Function to format breadcrumb display text
  const formatBreadcrumbText = (
    value: string,
    index: number,
    isLast: boolean = false
  ) => {
    const decodedValue = decodeURIComponent(value);

    // Check if this is a walkdown details page (path: /walkdowns/{id} or /walkdowns/{id1},{id2})
    if (index === 1 && pathnames[0] === "walkdowns" && pathnames.length >= 2) {
      const walkdownIds = pathnames[1].split(",");

      // Use Redux store data if current walkdown IDs match the URL
      // if (walkdownInfo.currentWalkdownIds.length > 0 &&
      //     walkdownInfo.currentWalkdownNumbers.length > 0 &&
      //     walkdownIds.every(id => walkdownInfo.currentWalkdownIds.includes(id))) {
      //     // Return formatted walkdown number(s)
      //     return `Walkdown ${walkdownInfo.currentWalkdownNumbers.join(', ')}`;
      // }

      // Still loading walkdown details
      return "";
    }

    // Generated by Copilot - Check if this is a device details page (path: /devices/{id})
    if (index === 1 && pathnames[0] === "devices" && pathnames.length >= 2) {
      const deviceId = pathnames[1];
      console.log("Bread: deviceid", deviceId, deviceInfo);
      // If a restricted-view label override is provided (serial number), use it
      if (overrideDeviceLabel) return overrideDeviceLabel;
      return deviceInfo.data?.tag_no || deviceInfo.data?.serial_number;
    }

    if (
      index === 1 &&
      pathnames[0] === "turnarounds" &&
      pathnames.length >= 2
    ) {
      return turnAroundDetail?.name;
    }

    // Search-results path
    if (
      index === 0 &&
      pathnames[0] === "search-results" &&
      pathnames.length >= 2
    ) {
      return null;
    }
    if (
      index === 1 &&
      pathnames[0] === "search-results" &&
      pathnames.length >= 2
    ) {
      return decodedValue;
    }

    // Other common path segments
    const pathMappings: { [key: string]: string } = {
      devices: t("devices", "Devices"),
      dashboard: t("dashboard", "Dashboard"),
      reports: t("reports", "Reports"),
      settings: t("settings", "Settings"),
      "my-tasks": t("myTasks", "My Tasks"),
      walkdowns: t("walkdowns", "Walkdowns"),
      "valve-condition-monitoring": t(
        "valveConditionMonitoring",
        "Valve Condition Monitoring"
      ),
      "site-documentation": t("siteDocumentation", "Site Documentation"), 
    };

    return pathMappings[decodedValue.toLowerCase()] || decodedValue;
  };

  // Hide breadcrumbs for unauthenticated users
  // if (!isAuthenticated) return null;

  return (
    <>
      {!isMobile && (
        <Box
          className="navbar-breadcrumb-section"
          component="nav"
          aria-label="breadcrumb"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateRows: "auto auto",
              gridRowGap: "16px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center", // Vertically center
                height: "100%",
                marginTop: "30px", // Ensure full height for vertical centering
              }}
            >
              <Breadcrumbs
                separator={
                  <NavigateNextIcon fontSize="small" sx={{ mx: 0, p: 0 }} />
                }
                className="navbar-breadcrumb-container"
                sx={{
                  "& .MuiBreadcrumbs-ol": {
                    display: "flex",
                    alignItems: "center",
                    margin: 0,
                    padding: 0,
                    gap: 0,
                  },
                  "& .MuiBreadcrumbs-li": {
                    margin: 0,
                    padding: 0,
                  },
                  "& .MuiBreadcrumbs-separator": {
                    margin: 0,
                    padding: 0,
                    marginLeft: "2px",
                    marginRight: "2px",
                  },
                  mb: 2,
                }}
              >
                <Link
                  onClick={(e) => {
                    e.preventDefault();
                    safeNavigate("/devices");
                  }}
                  className="navbar-breadcrumb-link navbar-myaccount no-underline"
                  sx={{ typography: "body2", fontWeight: 500, fontSize: 14 }}
                >
                  {t("myAccount", "My Account")}
                </Link>

                {pathnames.map((value, index) => {
                  const to = `/${pathnames.slice(0, index + 1).join("/")}`;
                  const isLast = index === pathnames.length - 1;
                  const displayText = formatBreadcrumbText(
                    value,
                    index,
                    isLast
                  );

                  // Generated by Copilot
                  // Skip rendering if formatBreadcrumbText returns null (e.g., for "search-results" segment)
                  if (displayText === null) {
                    return null;
                  }

                  return isLast ? (
                    <Typography
                      key={to}
                      className="navbar-breadcrumb-current"
                      sx={{ fontSize: 14 }}
                    >
                      {displayText}
                    </Typography>
                  ) : (
                    <Link
                      key={to}
                      onClick={(e) => {
                        e.preventDefault();
                        safeNavigate(to);
                      }}
                      underline="hover"
                      className="navbar-breadcrumb-link"
                      sx={{
                        typography: "body2",
                        fontWeight: 500,
                        fontSize: 14,
                      }}
                    >
                      {displayText}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            </Box>
            {/* Page Header */}
          </Box>
        </Box>
      )}
    </>
  );
};

export default BreadcrumbsComponent;
